// @flow
import * as React from 'react';
import type { PublicPage } from 'api/website';
import { humanDateTime } from 'utils/date';
import { getRgbaString } from 'utils/style';
import { AmpContentBlock } from 'components/Cms/getContentBlock';
import SocialButtons from 'components/CmsSocialButtons';
import AmpPostSearchToolbar from 'components/AmpPostSearchToolbar';
import { AmpCmsRecommendedJobsBlock } from 'components/Cms/Amp';
import Box from 'components/Box';
import Link, { ConditionalLink } from 'components/Link';
import { PrimaryButtonLink, SecondaryButtonLink } from 'components/Button';
import { H1, H4, HeadingSmall, Body, BodySmall } from 'components/Text';
import { FilestackImage } from 'components/Image';
import Grid, { GridItem } from 'components/Grid';
import { maxWidth, gutterWidth, colors } from '../theme';

const AmpContentPage = ({
  content,
  loggedIn,
  hasGradProgram,
  pageUrl,
  hasPostSearchToolbar,
  searchMode,
  onlySearchResults
}: {
  content: PublicPage,
  loggedIn: boolean,
  hasGradProgram: boolean,
  pageUrl: string,
  hasPostSearchToolbar: boolean,
  searchMode: boolean,
  onlySearchResults: ?boolean
}) => {
  const homePageUrl = content.subsite.homePage
    ? content.subsite.homePage.url
    : '';
  const resultsPage = 0;
  if (onlySearchResults) {
    return null;
  }
  return (
    <>
      {hasPostSearchToolbar && (
        <AmpPostSearchToolbar
          content={content}
          searchMode={searchMode}
          homePage={content.subsite.homePage}
        />
      )}
      {!(content.heroSearch && content.heroSearch.onlyResults) && (
        <Box
          position="relative"
          maxWidth={maxWidth}
          py={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
          m="0 auto"
          data-qa-id="amp-content"
          textAlign={content.type === 'PAGE' && 'center'}
        >
          <Grid>
            <GridItem columnStart={[1, 1, 1, 2]} columnEnd={[13, 13, 13, 12]}>
              {searchMode ? (
                <>
                  <amp-list
                    id="article_results_list"
                    layout="fixed-height"
                    height="450"
                    src={`/data/website/search/posts/${
                      content.subsite.id
                    }/${USER_LOCALE}/${resultsPage}`}
                    items="results.hits"
                    binding="always"
                  >
                    <template type="amp-mustache">
                      <Box
                        display="inline-block"
                        width={[1, 1 / 2, 1 / 3]}
                        verticalAlign="top"
                        maxWidth={[320, 360]}
                        mx="auto"
                        px={[0, gutterWidth / 2]}
                        py={gutterWidth / 2}
                        textAlign="left"
                      >
                        <Box
                          component="a"
                          href={'/{{url}}'}
                          display="block"
                          mb={24}
                          borderRadius={8}
                          overflow="hidden"
                          bg={colors.platinum}
                        >
                          {/* eslint-disable-next-line react/style-prop-object */}
                          <div data-style="padding-bottom: 56.25%; width: 100%; height: 100%; background-size: cover; background-position: center; background-image: url(https://process.filestackapi.com/quality=value:70/cache=expiry:31536000/compress/no_metadata/{{thumbnail.handle}})" />
                        </Box>
                        <H4 mt={0} mb={16} lineHeight={1.375}>
                          <a href={'/{{url}}'}>{'{{title}}'}</a>
                        </H4>
                      </Box>
                    </template>
                  </amp-list>
                  <Box
                    position="relative"
                    zIndex="1"
                    pt={100}
                    mt={-100}
                    backgroundImage={`linear-gradient(to top, ${
                      colors.fog
                    } 0%, ${colors.fog} 50%, rgba(${getRgbaString(
                      colors.fog,
                      0
                    )}) 100%)`}
                  >
                    <SecondaryButtonLink
                      width="100%"
                      size="large"
                      href={`/${pageUrl}?searchMode=true&pq=`}
                    >
                      {getText('Show More')}
                    </SecondaryButtonLink>
                  </Box>
                </>
              ) : (
                <>
                  <H1>{content.title}</H1>
                  {(content.author ||
                    (content.postedDate && content.showDate)) && (
                    <Box mt={4}>
                      {content.author && content.author.name && (
                        <H4 data-qa-id="author-name" m={0} lineHeight={1}>
                          <ConditionalLink
                            data-qa-id="author-name-link"
                            link={
                              homePageUrl && content.author.slug
                                ? `/${homePageUrl}?author.slug=${
                                    content.author.slug
                                  }&searchMode=true&pq=`
                                : ''
                            }
                          >
                            <Body lineHeight={1.33}>{content.author.name}</Body>
                          </ConditionalLink>
                        </H4>
                      )}
                      {content.showDate && (
                        <BodySmall tag="time" dateTime={content.postedDate}>
                          {humanDateTime(content.postedDate)}
                        </BodySmall>
                      )}
                    </Box>
                  )}
                  {content.headerImage && (
                    <FilestackImage
                      data={content.headerImage}
                      alt={content.headerImage.altText || content.title}
                      width={1152}
                      height={450}
                      fit="crop"
                      layout="responsive"
                    />
                  )}
                  {content.contentBlocks.slice(0, 10).map(block =>
                    block.type === 'ANCHOR' ? (
                      <div
                        id={`content-anchor-${block.data.anchor}`}
                        key={block.id}
                      />
                    ) : (
                      <Box
                        my={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
                        key={block.id}
                        id={`cms-block-${block.id}`}
                        display={
                          block.type === 'FREE_CODE' && !block.data.isVisible
                        }
                      >
                        <AmpContentBlock
                          block={block}
                          loggedIn={loggedIn}
                          hasGradProgram={hasGradProgram}
                          pageUrl={pageUrl}
                        />
                      </Box>
                    )
                  )}

                  {content.contentBlocks.length > 10 && (
                    <Box mt={64}>
                      <PrimaryButtonLink href={`/${pageUrl}`}>
                        {getText('View Full Page')}
                      </PrimaryButtonLink>
                    </Box>
                  )}
                  {content.hasRecommendedJobs && (
                    <AmpCmsRecommendedJobsBlock
                      headline={content.recommendedJobsHeadline}
                    />
                  )}
                  {(content.hasSocialSharing ||
                    (content.subsite.canSubscribe && content.type === 'POST') ||
                    (content.tags && content.tags.length > 0)) && (
                    <Box mt={64} id="subscribe">
                      {(content.hasSocialSharing ||
                        (content.subsite.canSubscribe &&
                          content.type === 'POST')) && (
                        <Box classes={['flex']} m="-6px" mt={32}>
                          {content.hasSocialSharing && (
                            <SocialButtons
                              content={content}
                              message={content.metaDescription}
                              title={content.title}
                              subsite={content.subsite.name}
                            />
                          )}
                          {content.subsite.canSubscribe &&
                            content.type === 'POST' && (
                              <Box m="6px">
                                <Box
                                  component={SecondaryButtonLink}
                                  classes={['flex', 'align-center']}
                                  height={32}
                                  size="small"
                                  href={`/${pageUrl}?subscribe=true#subscribe`}
                                >
                                  {getText('Subscribe')}
                                </Box>
                              </Box>
                            )}
                        </Box>
                      )}
                      {content.tags && content.tags.length > 0 && (
                        <Box
                          mt={32}
                          classes={['flex']}
                          component={HeadingSmall}
                        >
                          {getText('Tags: ')}
                          {content.tags.map(tag => (
                            <Box ml={12}>
                              <Box
                                component={homePageUrl ? Link : undefined}
                                href={
                                  homePageUrl
                                    ? `/${homePageUrl}?tags.slug=${
                                        tag.slug
                                      }&searchMode=true&pq=`
                                    : undefined
                                }
                              >
                                {tag.tag}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </GridItem>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default AmpContentPage;
